/* Phase 87af (17/06/2026) — Import XLSX Nayax (rapport quotidien par machine).
 *
 * Workflow simplifié vs CSV (NayaxCsvImport) :
 *  - L'admin sélectionne le distributeur (pas de matching auto par nom machine)
 *  - Upload du XLSX exporté du portail Nayax (DynamicTransactionsMonitorMega)
 *  - Preview : affiche nb transactions, machines détectées, slots résolus/non
 *  - Import : déduit le stock via FIFO du lot actif pour chaque produit mappé
 *  - Dédup via Sale.external_id = Transaction ID Nayax
 *
 * Mapping slot → produit : utilise `distributor.displayed_product_rails` (grille
 * de répartition existante). Aucune nouvelle structure de données.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Upload,
  FileSpreadsheet,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Eye,
  PlayCircle,
} from "lucide-react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

export default function NayaxXlsxImport() {
  const [distributors, setDistributors] = useState([]);
  const [distId, setDistId] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/distributors");
        // Filtre : distributeurs automatiques avec au moins quelques rails configurés
        const eligible = (data || [])
          .filter((d) => d.type === "AUTOMATIQUE")
          .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setDistributors(eligible);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  const selectedDist = useMemo(() => distributors.find((d) => d.id === distId), [distributors, distId]);
  const railsCount = useMemo(() => Object.keys(selectedDist?.displayed_product_rails || {}).length, [selectedDist]);

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(null);
    setResult(null);
  };

  const doPreview = async () => {
    if (!file || !distId) {
      toast.error("Sélectionne un distributeur et un fichier");
      return;
    }
    setPreviewing(true);
    setPreview(null);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/nayax/xlsx/preview?distributor_id=${distId}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'analyse");
    } finally {
      setPreviewing(false);
    }
  };

  const doImport = async ({ dryRun = false } = {}) => {
    if (!file || !distId) return;
    setImporting(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const url = `/nayax/xlsx/import?distributor_id=${distId}${dryRun ? "&dry_run=true" : ""}`;
      const { data } = await api.post(url, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult({ ...data, dryRun });
      if (!dryRun) {
        toast.success(`Import terminé : ${data.created} ventes créées, ${data.duplicates} doublons, ${data.errors} erreurs`);
      } else {
        toast.success(`Dry-run : ${data.created} ventes seraient créées`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'import");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="nayax-xlsx-page">
      <div className="flex items-center gap-3">
        <Link to="/parametres" className="text-[#8C6B2F] hover:underline text-sm flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Paramètres
        </Link>
      </div>

      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-lg bg-[#FBF4E9] flex items-center justify-center flex-shrink-0">
          <FileSpreadsheet className="w-6 h-6 text-[#8C6B2F]" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-[#1A1C1A]">Import XLSX Nayax (inventaire)</h1>
          <p className="text-sm text-[#575A57] mt-1">
            Importe le rapport quotidien <code>DynamicTransactionsMonitorMega</code> du portail Nayax pour déduire automatiquement
            les ventes du stock du distributeur sélectionné. Mapping basé sur le slot (MDB Code) via la grille de répartition.
          </p>
        </div>
      </div>

      <Card>
        <CardContent className="p-6 space-y-4">
          <div>
            <Label className="mb-1.5 block font-medium">1. Distributeur cible</Label>
            <Select value={distId} onValueChange={setDistId}>
              <SelectTrigger className="max-w-md" data-testid="nayax-xlsx-dist-select">
                <SelectValue placeholder="Sélectionner un distributeur automatique…" />
              </SelectTrigger>
              <SelectContent>
                {distributors.map((d) => {
                  const rc = Object.keys(d.displayed_product_rails || {}).length;
                  return (
                    <SelectItem key={d.id} value={d.id}>
                      {d.name} {rc > 0 ? `— ${rc} slot(s)` : "— ⚠ aucun slot configuré"}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            {selectedDist && railsCount === 0 && (
              <p className="text-xs text-[#D24E32] mt-1.5 flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5" />
                Ce distributeur n'a aucun slot mappé. Va dans Distributeurs → Modifier → grille slots → coche les produits et renseigne leur numéro de slot.
              </p>
            )}
          </div>

          <div>
            <Label className="mb-1.5 block font-medium">2. Fichier XLSX</Label>
            <input
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={onFileChange}
              data-testid="nayax-xlsx-file-input"
              className="block w-full text-sm text-[#575A57] file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-[#FBF4E9] file:text-[#8C6B2F] file:font-medium hover:file:bg-[#F0E6D2]"
            />
            {file && (
              <p className="text-xs text-[#575A57] mt-1.5">
                <FileSpreadsheet className="inline w-3.5 h-3.5 mr-1" />
                {file.name} · {(file.size / 1024).toFixed(1)} KB
              </p>
            )}
          </div>

          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              onClick={doPreview}
              disabled={!file || !distId || previewing}
              data-testid="nayax-xlsx-preview-btn"
            >
              {previewing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Eye className="w-4 h-4 mr-1.5" />}
              Analyser
            </Button>
          </div>
        </CardContent>
      </Card>

      {preview && (
        <Card>
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-[#2D6A4F]" />
              <h2 className="font-semibold text-lg">Aperçu du fichier</h2>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label="Transactions" value={preview.row_count} />
              <Stat label="Résolues" value={preview.resolved_count} ok />
              <Stat label="Non résolues" value={preview.unresolved_count} warn={preview.unresolved_count > 0} />
              <Stat label="Déjà importées" value={preview.already_imported || 0} />
            </div>

            {preview.machines_detected.length > 0 && (
              <div className="text-sm">
                <span className="text-[#575A57]">Machine(s) détectée(s) : </span>
                <strong>{preview.machines_detected.join(", ")}</strong>
              </div>
            )}

            {preview.unmatched_slots.length > 0 && (
              <div className="bg-[#FCEEEB] border border-[#D24E32]/40 rounded p-3">
                <p className="text-sm font-medium text-[#D24E32] flex items-center gap-1.5">
                  <AlertTriangle className="w-4 h-4" />
                  {preview.unmatched_slots.length} slot(s) non mappé(s) → ces ventes seront IGNORÉES
                </p>
                <ul className="text-xs text-[#575A57] mt-2 space-y-1">
                  {preview.unmatched_slots.map((u) => (
                    <li key={u.slot} className="font-mono">
                      <span className="font-bold text-[#1A1C1A]">Slot {u.slot}</span>
                      {" · "}{u.count} vente(s) · "{u.product_label}"
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-[#575A57] mt-2 italic">
                  Va dans Distributeurs → {selectedDist?.name} → grille slots pour les mapper.
                </p>
              </div>
            )}

            {preview.sample_transactions.length > 0 && (
              <div>
                <p className="text-sm font-medium text-[#1A1C1A] mb-2">Échantillon (10 premières) :</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="text-[#575A57] border-b">
                      <tr>
                        <th className="text-left p-2">Date</th>
                        <th className="text-left p-2">Slot</th>
                        <th className="text-left p-2">Produit Nayax</th>
                        <th className="text-left p-2">Résolu vers</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.sample_transactions.map((s) => (
                        <tr key={s.transaction_id} className="border-b border-[#F0F0EB]">
                          <td className="p-2 font-mono">{s.sold_at?.slice(0, 16).replace("T", " ")}</td>
                          <td className="p-2 font-mono font-bold">{s.mdb_code}</td>
                          <td className="p-2 text-[#575A57]">{s.product_label_raw?.slice(0, 50)}</td>
                          <td className="p-2">
                            {s.resolved_product ? (
                              <Badge variant="outline" className="bg-[#EBF2E9] text-[#2D6A4F] border-[#2D6A4F]/40">
                                {s.resolved_product.sku} · {s.resolved_product.name?.slice(0, 30)}
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="bg-[#FCEEEB] text-[#D24E32] border-[#D24E32]/40">
                                ⚠ slot non mappé
                              </Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="flex gap-2 pt-2 border-t border-[#F0F0EB]">
              <Button
                variant="outline"
                onClick={() => doImport({ dryRun: true })}
                disabled={importing}
                data-testid="nayax-xlsx-dryrun-btn"
              >
                {importing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Eye className="w-4 h-4 mr-1.5" />}
                Test à blanc (dry-run)
              </Button>
              <Button
                onClick={() => doImport({ dryRun: false })}
                disabled={importing || preview.resolved_count === 0}
                className="bg-[#2D6A4F] hover:bg-[#1F4E3D] text-white"
                data-testid="nayax-xlsx-import-btn"
              >
                {importing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <PlayCircle className="w-4 h-4 mr-1.5" />}
                Importer {preview.resolved_count} vente(s)
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {result && (
        <Card>
          <CardContent className="p-6 space-y-3">
            <div className="flex items-center gap-2">
              {result.errors > 0 ? (
                <AlertTriangle className="w-5 h-5 text-[#D24E32]" />
              ) : (
                <CheckCircle2 className="w-5 h-5 text-[#2D6A4F]" />
              )}
              <h2 className="font-semibold text-lg">
                {result.dryRun ? "Test à blanc — aucun changement" : "Résultat de l'import"}
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label={result.dryRun ? "Seraient créées" : "Créées"} value={result.created} ok />
              <Stat label="Doublons (ignorés)" value={result.duplicates} />
              <Stat label="Erreurs" value={result.errors} warn={result.errors > 0} />
              <Stat label="Total" value={result.total_rows} />
            </div>
            {result.error_details && result.error_details.length > 0 && (
              <div>
                <p className="text-sm font-medium text-[#D24E32] mb-1.5">
                  Détail des erreurs ({result.error_details.length}) :
                </p>
                <ul className="text-xs text-[#575A57] space-y-1 max-h-60 overflow-y-auto bg-[#FCEEEB] p-3 rounded">
                  {result.error_details.map((e, i) => (
                    <li key={i} className="font-mono">
                      L{e.line}{e.tx_id ? ` · tx ${e.tx_id}` : ""}{e.slot ? ` · slot ${e.slot}` : ""} : {e.reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

const Stat = ({ label, value, ok = false, warn = false }) => (
  <div
    className={`p-3 rounded border ${
      ok ? "bg-[#EBF2E9] border-[#2D6A4F]/40" : warn ? "bg-[#FCEEEB] border-[#D24E32]/40" : "bg-[#FAFAF7] border-[#E5E5E0]"
    }`}
  >
    <div
      className={`text-2xl font-bold ${ok ? "text-[#2D6A4F]" : warn ? "text-[#D24E32]" : "text-[#1A1C1A]"}`}
    >
      {value}
    </div>
    <div className="text-xs text-[#575A57] mt-0.5">{label}</div>
  </div>
);
